import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, BadgeCheck, BookOpen, BriefcaseBusiness, CalendarClock,
  Check, GraduationCap, Rocket, Search, Shield, Sparkles, Users,
} from 'lucide-react';
import StripeCheckout from '../components/StripeCheckout';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const PRODUCT_DETAILS = {
  resume_builder_lifetime: {
    eyebrow: 'Own the tool',
    icon: BookOpen,
    features: ['Lifetime Resume Builder access', 'AI writing suggestions', 'Professional templates'],
    destination: '/resume-builder',
  },
  interview_prep_90: {
    eyebrow: '90-day access',
    icon: GraduationCap,
    features: ['Monthly rotating classroom lessons', 'AI practice questions', 'Answer coaching and feedback'],
    destination: '/interview-prep',
  },
  candidate_spotlight_30: {
    eyebrow: '30-day visibility',
    icon: Sparkles,
    features: ['Priority candidate-search placement', 'Visible Spotlight badge', 'Starts immediately after payment'],
    destination: '/profile',
  },
  career_launch_90: {
    eyebrow: 'Best value',
    icon: Rocket,
    featured: true,
    features: ['Lifetime AI Resume Studio', '90-day Interview Academy', '30-day Candidate Spotlight'],
    destination: '/candidate/dashboard',
  },
  job_spotlight_7: {
    eyebrow: '7-day campaign',
    icon: Rocket,
    features: ['Priority marketplace placement', 'Boosted listing treatment', 'Extends an active campaign'],
  },
  job_campaign_30: {
    eyebrow: '30-day campaign',
    icon: CalendarClock,
    features: ['Regional hiring visibility', 'Priority marketplace placement', 'Best for active hiring'],
  },
  job_campaign_60: {
    eyebrow: '60-day campaign',
    icon: BriefcaseBusiness,
    features: ['Extended listing priority', 'Designed for multiple interview rounds', 'Campaign days stack'],
  },
  job_campaign_90: {
    eyebrow: '90-day campaign',
    icon: Search,
    featured: true,
    features: ['Longest priority placement', 'Built for harder-to-fill roles', 'Campaign days stack'],
  },
};

function ProductCard({ item, active, adminMode, onBuy, onOpen }) {
  const details = PRODUCT_DETAILS[item.sku] || {};
  const Icon = details.icon || Sparkles;
  const canOpen = active && details.destination;
  return (
    <article className={`relative flex h-full flex-col border bg-white p-6 ${details.featured ? 'border-blue-600 shadow-sm' : 'border-gray-200'}`} data-testid={`marketplace-product-${item.sku}`}>
      {details.featured && <Badge className="absolute right-4 top-4 bg-blue-700">Recommended</Badge>}
      <div className="mb-5 flex h-10 w-10 items-center justify-center border border-gray-200 bg-gray-50 text-gray-900">
        <Icon size={21} />
      </div>
      <p className="mb-1 text-xs font-semibold uppercase text-blue-700">{details.eyebrow}</p>
      <h3 className="text-xl font-semibold">{item.name}</h3>
      <p className="mt-2 min-h-12 text-sm leading-6 text-gray-600">{item.description}</p>
      <div className="mt-5 flex items-end gap-2">
        <span className="text-3xl font-bold">{adminMode ? '$0' : `$${(item.amount / 100).toFixed(0)}`}</span>
        <span className="pb-1 text-sm text-gray-500">{adminMode ? 'admin test' : 'one time'}</span>
      </div>
      <ul className="my-6 space-y-3 text-sm text-gray-700">
        {(details.features || []).map((feature) => (
          <li key={feature} className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-green-700" />{feature}</li>
        ))}
      </ul>
      <Button className="mt-auto w-full" variant={details.featured ? 'default' : 'outline'} onClick={canOpen ? onOpen : onBuy} data-testid={`buy-${item.sku}`}>
        {canOpen ? 'Open Tool' : active ? 'Extend Access' : adminMode ? 'Activate Test Access' : 'Choose Product'}
      </Button>
    </article>
  );
}

export default function Marketplace({ user }) {
  const navigate = useNavigate();
  const storedRole = localStorage.getItem('role');
  const role = user?.role || storedRole;
  const isAdmin = role === 'admin';
  const [audience, setAudience] = useState(role === 'company' ? 'company' : 'candidate');
  const [catalog, setCatalog] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [purchases, setPurchases] = useState({ purchases: [], subscriptions: [], entitlements: [] });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [checkoutData, setCheckoutData] = useState(null);
  const [loadingSku, setLoadingSku] = useState('');

  const loadMarketplace = useCallback(async () => {
    try {
      const requests = [api.get('/marketplace/catalog'), api.get('/purchases/my-purchases')];
      if (role === 'company' || isAdmin) requests.push(api.get('/marketplace/jobs'));
      const [catalogResponse, purchaseResponse, jobsResponse] = await Promise.all(requests);
      setCatalog(catalogResponse.data.items || []);
      setPurchases(purchaseResponse.data || { purchases: [], subscriptions: [], entitlements: [] });
      setJobs(jobsResponse?.data?.jobs || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Marketplace could not load');
    }
  }, [isAdmin, role]);

  useEffect(() => { loadMarketplace(); }, [loadMarketplace]);

  const visibleProducts = useMemo(
    () => catalog.filter((item) => item.role === audience),
    [audience, catalog]
  );

  const hasAccess = (item) => {
    const now = new Date();
    const entitlements = purchases.entitlements || [];
    if (item.kind === 'resume_builder') return entitlements.some((entry) => entry.product_type === 'resume_builder' && (!entry.expires_at || new Date(entry.expires_at) > now));
    if (item.kind === 'interview_prep') return entitlements.some((entry) => entry.product_type === 'interview_prep' && (!entry.expires_at || new Date(entry.expires_at) > now));
    if (item.kind === 'career_launch') return entitlements.some((entry) => entry.source_sku === 'career_launch_90' && (!entry.expires_at || new Date(entry.expires_at) > now));
    if (item.kind === 'candidate_spotlight') return (purchases.purchases || []).some((entry) => entry.fulfillment?.candidate_spotlight_until && new Date(entry.fulfillment.candidate_spotlight_until) > now);
    return false;
  };

  const startCheckout = async (item, targetId = null) => {
    setLoadingSku(item.sku);
    try {
      const response = await api.post('/marketplace/checkout', { sku: item.sku, target_id: targetId });
      if (response.data.admin_bypass) {
        toast.success(`${item.name} activated in admin test mode`);
        setSelectedProduct(null);
        setSelectedJobId('');
        await loadMarketplace();
        return;
      }
      setCheckoutData(response.data);
      setSelectedProduct(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout could not start');
    } finally {
      setLoadingSku('');
    }
  };

  const chooseProduct = (item) => {
    if (item.kind === 'job_boost') {
      setSelectedProduct(item);
      setSelectedJobId(jobs[0]?.id || '');
      return;
    }
    startCheckout(item);
  };

  const confirmPurchase = async (paymentIntent) => {
    try {
      await api.post('/marketplace/confirm', { payment_intent_id: paymentIntent.id });
      toast.success('Purchase verified and activated');
      setCheckoutData(null);
      setSelectedJobId('');
      await loadMarketplace();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment succeeded, but activation needs review');
    }
  };

  const handleBack = () => navigate(role === 'admin' ? '/admin/dashboard' : role === 'company' ? '/company/dashboard' : '/candidate/dashboard');

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 border-b bg-white">
        <div className="container mx-auto flex items-center justify-between px-4 py-4 md:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Back"><ArrowLeft size={19} /></Button>
            <img src="/logo.svg" alt="The Interview" className="h-9 w-9" />
            <div><h1 className="text-xl font-bold">Marketplace</h1><p className="text-xs text-gray-500">Tools that activate when you buy them</p></div>
          </div>
          <span className="hidden text-sm text-gray-600 sm:block">{user?.full_name}</span>
        </div>
      </header>

      <main>
        <section className="border-b bg-white">
          <div className="container mx-auto px-4 py-10 md:px-6">
            <div className="max-w-3xl">
              <p className="mb-2 text-sm font-semibold text-blue-700">The Interview Marketplace</p>
              <h2 className="text-3xl font-bold md:text-4xl">Buy momentum, not shelfware.</h2>
              <p className="mt-3 text-base leading-7 text-gray-600">Every paid product below activates a real tool, timed access, or marketplace placement immediately after Stripe verifies payment.</p>
            </div>
            {isAdmin && <div className="mt-6 border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950"><strong>Admin test mode:</strong> Stripe is bypassed, while the same fulfillment rules still run.</div>}
            {isAdmin && (
              <div className="mt-6 inline-flex border bg-gray-100 p-1" aria-label="Marketplace audience">
                <Button size="sm" variant={audience === 'candidate' ? 'default' : 'ghost'} onClick={() => setAudience('candidate')}>Candidate products</Button>
                <Button size="sm" variant={audience === 'company' ? 'default' : 'ghost'} onClick={() => setAudience('company')}>Company products</Button>
              </div>
            )}
          </div>
        </section>

        <section className="container mx-auto px-4 py-10 md:px-6">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div><h2 className="text-2xl font-bold">{audience === 'company' ? 'Hiring campaigns' : 'Career tools'}</h2><p className="mt-1 text-sm text-gray-600">{audience === 'company' ? 'Select the job you want to promote during checkout.' : 'Choose one focused tool or the complete career sprint.'}</p></div>
            {audience === 'company' && <Badge variant="outline">{jobs.length} job{jobs.length === 1 ? '' : 's'} available</Badge>}
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {visibleProducts.map((item) => (
              <ProductCard
                key={item.sku}
                item={item}
                active={hasAccess(item)}
                adminMode={isAdmin}
                onBuy={() => chooseProduct(item)}
                onOpen={() => {
                  const destination = PRODUCT_DETAILS[item.sku]?.destination;
                  if (destination) navigate(destination);
                  else handleBack();
                }}
              />
            ))}
          </div>
        </section>

        <section className="border-y bg-white">
          <div className="container mx-auto px-4 py-10 md:px-6">
            <h2 className="text-2xl font-bold">Included with your account</h2>
            <p className="mt-1 text-sm text-gray-600">These are live platform workflows, not checkout add-ons.</p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {audience === 'candidate' ? (
                <>
                  <div className="flex items-center justify-between border p-5"><div className="flex gap-3"><Users className="text-blue-700" /><div><h3 className="font-semibold">Industry Mentors</h3><p className="text-sm text-gray-600">Browse profiles, availability, and session pricing.</p></div></div><Button variant="outline" onClick={() => navigate('/mentorship')}>Browse Mentors</Button></div>
                  <div className="flex items-center justify-between border p-5"><div className="flex gap-3"><Shield className="text-green-700" /><div><h3 className="font-semibold">Candidate Verification</h3><p className="text-sm text-gray-600">Choose a screening package and track its status.</p></div></div><Button variant="outline" onClick={() => navigate('/background-check')}>View Checks</Button></div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between border p-5"><div className="flex gap-3"><Search className="text-blue-700" /><div><h3 className="font-semibold">Candidate Search</h3><p className="text-sm text-gray-600">Filter public profiles by role, location, industry, and experience.</p></div></div><Button variant="outline" onClick={() => navigate('/browse-candidates')}>Search Candidates</Button></div>
                  <div className="flex items-center justify-between border p-5"><div className="flex gap-3"><BadgeCheck className="text-purple-700" /><div><h3 className="font-semibold">Phase 1 AI Interviews</h3><p className="text-sm text-gray-600">Configure questions, scoring thresholds, and human review.</p></div></div><Button variant="outline" onClick={() => navigate('/company/dashboard')}>Configure Interviews</Button></div>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-lg" data-testid="job-campaign-dialog">
          <DialogHeader><DialogTitle>Choose a job to promote</DialogTitle><DialogDescription>{selectedProduct?.name} activates only for the listing you select.</DialogDescription></DialogHeader>
          {jobs.length ? (
            <div className="space-y-4">
              <label className="block text-sm font-medium" htmlFor="marketplace-job">Active job</label>
              <select id="marketplace-job" className="w-full border px-3 py-3" value={selectedJobId} onChange={(event) => setSelectedJobId(event.target.value)} data-testid="marketplace-job-select">
                {jobs.map((job) => <option value={job.id} key={job.id}>{job.title}{job.company_name ? ` - ${job.company_name}` : ''}</option>)}
              </select>
              <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setSelectedProduct(null)}>Cancel</Button><Button disabled={!selectedJobId || loadingSku === selectedProduct?.sku} onClick={() => startCheckout(selectedProduct, selectedJobId)}>{loadingSku ? 'Starting...' : isAdmin ? 'Activate Campaign' : `Continue - $${((selectedProduct?.amount || 0) / 100).toFixed(0)}`}</Button></div>
            </div>
          ) : (
            <div className="border bg-gray-50 p-5 text-sm"><p className="font-semibold">Create a job before starting a campaign.</p><p className="mt-1 text-gray-600">Campaign placement needs a published listing to promote.</p><Button className="mt-4" onClick={() => navigate('/company/dashboard')}>Create a Job</Button></div>
          )}
        </DialogContent>
      </Dialog>

      {checkoutData && (
        <StripeCheckout
          isOpen
          onClose={() => setCheckoutData(null)}
          amount={checkoutData.amount}
          description={checkoutData.description}
          clientSecret={checkoutData.client_secret}
          onSuccess={confirmPurchase}
        />
      )}
    </div>
  );
}
